(ns common.connection-lifetime
  "Shared connection lifetime options and utility functions.
   Used by tenant admin settings (max lifetime) and user token creation.

   Values are stored in the database as keywords (e.g., :connection-lifetime/y1).
   nil means 'not set' (use tenant default), :connection-lifetime/unlimited means 'never expires'.")

(def ^:private lifetime-options-base
  "Lifetime options ordered from shortest to longest"
  [{:value :connection-lifetime/h1        :label "1 hour"}
   {:value :connection-lifetime/h4        :label "4 hours"}
   {:value :connection-lifetime/h8        :label "8 hours"}
   {:value :connection-lifetime/d1        :label "1 day"}
   {:value :connection-lifetime/d3        :label "3 days"}
   {:value :connection-lifetime/w1        :label "1 week"}
   {:value :connection-lifetime/w2        :label "2 weeks"}
   {:value :connection-lifetime/w4        :label "4 weeks"}
   {:value :connection-lifetime/m3        :label "3 months"}
   {:value :connection-lifetime/m6        :label "6 months"}
   {:value :connection-lifetime/y1        :label "1 year"}
   {:value :connection-lifetime/y2        :label "2 years"}
   {:value :connection-lifetime/unlimited :label "Unlimited"}])

(def lifetime-options
  "Connection lifetime options with :key alias for dropdown components"
  (mapv #(assoc % :key (:value %)) lifetime-options-base))

(def lifetime-keys
  "Set of valid lifetime keywords"
  (set (map :value lifetime-options-base)))

(def ^:private key->ms
  "Lookup map from keyword to milliseconds.
   Unlimited is represented as ~200 years in ms — a concrete large number
   so that nil unambiguously means 'not configured'.
   Uses approximate month/year durations (30 days / 365 days)."
  {:connection-lifetime/h1        (* 1000 60 60)
   :connection-lifetime/h4        (* 1000 60 60 4)
   :connection-lifetime/h8        (* 1000 60 60 8)
   :connection-lifetime/d1        (* 1000 60 60 24)
   :connection-lifetime/d3        (* 1000 60 60 24 3)
   :connection-lifetime/w1        (* 1000 60 60 24 7)
   :connection-lifetime/w2        (* 1000 60 60 24 14)
   :connection-lifetime/w4        (* 1000 60 60 24 28)
   :connection-lifetime/m3        (* 1000 60 60 24 30 3)    ;; ~90 days
   :connection-lifetime/m6        (* 1000 60 60 24 30 6)    ;; ~180 days
   :connection-lifetime/y1        (* 1000 60 60 24 365)     ;; ~365 days
   :connection-lifetime/y2        (* 1000 60 60 24 365 2)   ;; ~730 days
   :connection-lifetime/unlimited (* 1000 60 60 24 365 200)}) ;; ~200 years

(def default-max-lifetime
  "Default maximum connection lifetime when tenant setting is nil (not configured).
   Used as a safe runtime default instead of treating nil as unlimited."
  :connection-lifetime/y1)

(defn resolve-tenant-max-lifetime
  "Resolve the effective tenant max lifetime.
   Returns the tenant's configured value, or default-max-lifetime if nil."
  [tenant-max-key]
  (or tenant-max-key default-max-lifetime))

(defn lifetime->ms
  "Convert a lifetime keyword to milliseconds.
   Returns nil if lifetime-key is nil (not configured).
   Returns a large number for :connection-lifetime/unlimited."
  [lifetime-key]
  (get key->ms lifetime-key))

(defn valid-lifetime-key?
  "Check if a lifetime keyword is valid"
  [lifetime-key]
  (contains? lifetime-keys lifetime-key))

(defn normalize-lifetime
  "Normalize a lifetime value to a valid keyword.
   Returns nil if value is nil. Throws if value is not a valid lifetime keyword."
  [value]
  (cond
    (nil? value) nil
    (valid-lifetime-key? value) value
    :else (throw (ex-info "Invalid connection lifetime value"
                          {:error/type :error.type/validation-error
                           :value      value}))))

(defn filter-options-by-max
  "Filter lifetime options to only include those <= max lifetime keyword.
   If max-lifetime-key is nil (not configured), returns all options.
   Used to limit user options based on tenant admin setting."
  [max-lifetime-key]
  (let [max-ms (lifetime->ms max-lifetime-key)]
    (if (nil? max-ms)
      ;; nil means not configured - show all options
      lifetime-options
      (filterv (fn [{:keys [value]}]
                 (let [ms (lifetime->ms value)]
                   (and (some? ms) (<= ms max-ms))))
               lifetime-options))))

(defn cap-lifetime
  "Cap a user's requested lifetime to not exceed tenant maximum.
   Returns the effective lifetime keyword."
  [user-lifetime-key tenant-max-key]
  (cond
    (nil? user-lifetime-key) tenant-max-key
    (nil? tenant-max-key) user-lifetime-key
    :else
    (let [user-ms (lifetime->ms user-lifetime-key)
          tenant-max-ms (lifetime->ms tenant-max-key)]
      (if (<= user-ms tenant-max-ms)
        user-lifetime-key
        tenant-max-key))))
